/* eslint-disable local/numeric-separators */

/**
 * Helper function to generate a hash on a string. Hashes should be
 * consistent over time (i.e. running this function over and over
 * returns the same hash every time) and unique across strings (i.e.
 * running this on different strings will never return the same hash).
 *
 * @param originalText The text from which a hash should be generated
 * @returns Hash string for the text
 */
export const generateSHA256Hash = async (originalText: string): Promise<string> => {
  const msgBuffer = new TextEncoder().encode(originalText);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

/**
 * Synchronous hash generator for strings
 *
 * @param originalString Text from which a hash should be generated
 * @param seed Optional number to use for a seed value
 * @returns The hash for the string (hex string)
 */
export const synchronousHash = (originalString: string, seed = 0) => {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;

  for (let i = 0, ch: number; i < originalString.length; i++) {
    ch = originalString.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
};

/**
 * Helper to generate a string that's unique enough for `key`
 * purposes. For real, unique strings, UUID should be used
 * instead.
 *
 * @returns Unique-ish string
 */
export const quickID = (): string => {
  return Math.random().toString(36).substring(2, 10);
};
